"use client";

import { useState, useEffect, useMemo } from "react";
import { useParams, useRouter, usePathname, useSearchParams } from "next/navigation";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { OfferListingCard } from "@/components/shared/OfferListingCard";
import { MobileEndDrawer } from "@/components/shared/MobileEndDrawer";
import { Pagination } from "@/components/shared/Pagination";
import { useLanguage } from "@/contexts/LanguageContext";
import type { ListedOffer } from "@/lib/offersApi";
import { loadOffersListClient } from "@/lib/offersCatalogClient";
import { OfferListingSkeletonGrid } from "@/components/shared/ListingCardSkeletons";
import {
  buildListingCanonicalPath,
  buildLocalizedListingUrl,
  decodeOffersListingFilters,
  encodeOffersListingSegments,
  listingSearchParamsPreserved,
  LISTING_ALL_TOKEN,
  parseListingSegmentsFromPathname,
  slugifyListingLabel,
} from "@/lib/listingFilterPath";
import { pathnameToCanonical } from "@/lib/i18nRouting";
import { buildOfferDetailPath } from "@/lib/serviceUrl";

const ITEMS_PER_PAGE = 12;

const OFFERS_LISTING_BASE = "/offers";

type SortByValue =
  | "newest"
  | "expiring"
  | "discount-high"
  | "discount-low";

const OFFER_SORT_VALUES: SortByValue[] = [
  "newest",
  "expiring",
  "discount-high",
  "discount-low",
];

const VALID_SORT_VALUES = new Set<SortByValue>(OFFER_SORT_VALUES);

function parseOfferTime(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

function FilterToolbarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

export default function OffersPage() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const routeParams = useParams();
  const { t, locale } = useLanguage();
  const resolvedLang: "en" | "ar" = locale === "ar" ? "ar" : "en";

  const pathFilterSegments = useMemo(() => {
    const raw = routeParams.filter;
    if (raw === undefined) return [] as string[];
    return Array.isArray(raw) ? raw.map(String) : [String(raw)];
  }, [routeParams.filter]);

  const sortOptions = useMemo(
    () =>
      [
        { value: "newest" as const, label: t("news.newestFirst") },
        { value: "expiring" as const, label: t("offers.expiringSoon") },
        { value: "discount-high" as const, label: t("offers.highestDiscount") },
        { value: "discount-low" as const, label: t("offers.lowestDiscount") },
      ] as const,
    [t]
  );

  const allOffersLabel = t("offers.allOffers");

  const [offers, setOffers] = useState<ListedOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortByValue>("newest");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [desktopCategoryOpen, setDesktopCategoryOpen] = useState(true);
  const [desktopSortOpen, setDesktopSortOpen] = useState(true);

  /** Avoid replacing the URL before filters are hydrated from the initial query string. */
  const [filtersReadyForUrlSync, setFiltersReadyForUrlSync] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const { offers: list } = await loadOffersListClient(resolvedLang);
        if (!cancelled) {
          setOffers(list);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setOffers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedLang]);

  useEffect(() => {
    if (loading) {
      setFiltersReadyForUrlSync(false);
      return;
    }
    const id = requestAnimationFrame(() => setFiltersReadyForUrlSync(true));
    return () => cancelAnimationFrame(id);
  }, [loading]);

  const categories = useMemo(() => {
    const types = new Set<string>();
    for (const o of offers) {
      if (o.category.trim()) types.add(o.category);
    }
    const sorted = [...types].sort((a, b) => a.localeCompare(b));
    return [allOffersLabel, ...sorted];
  }, [offers, allOffersLabel]);

  useEffect(() => {
    if (loading) return;

    const segmentsFromPath =
      pathFilterSegments.length > 0
        ? pathFilterSegments
        : parseListingSegmentsFromPathname(pathname, OFFERS_LISTING_BASE);

    if (segmentsFromPath.length > 0) {
      const decoded = decodeOffersListingFilters(segmentsFromPath);
      const catFromUrl = decoded.categoryId.trim();
      if (
        catFromUrl === LISTING_ALL_TOKEN ||
        catFromUrl === "all" ||
        !catFromUrl
      ) {
        setSelectedCategory(allOffersLabel);
      } else {
        const match = categories.find(
          (c) =>
            c !== allOffersLabel &&
            (slugifyListingLabel(c) === catFromUrl || c === catFromUrl),
        );
        if (match) setSelectedCategory(match);
      }

      if (VALID_SORT_VALUES.has(decoded.sortBy as SortByValue)) {
        setSortBy(decoded.sortBy as SortByValue);
      } else {
        setSortBy("newest");
      }
      return;
    }

    const cat = searchParams.get("category");
    if (!cat || cat === "all") {
      setSelectedCategory(allOffersLabel);
    } else if (categories.includes(cat)) {
      setSelectedCategory(cat);
    }

    const sortParam = searchParams.get("sort");
    if (sortParam && VALID_SORT_VALUES.has(sortParam as SortByValue)) {
      setSortBy(sortParam as SortByValue);
    } else {
      setSortBy("newest");
    }
  }, [
    loading,
    searchParams,
    categories,
    allOffersLabel,
    pathFilterSegments,
    pathname,
  ]);

  useEffect(() => {
    if (loading || !filtersReadyForUrlSync) return;

    const categoryForUrl =
      selectedCategory === allOffersLabel || !selectedCategory
        ? LISTING_ALL_TOKEN
        : slugifyListingLabel(selectedCategory);

    const encoded = encodeOffersListingSegments({
      categoryId: categoryForUrl,
      sortBy,
    });
    const canonicalTarget = buildListingCanonicalPath(
      OFFERS_LISTING_BASE,
      encoded,
    );

    const currentPath =
      pathnameToCanonical(pathname.split("?")[0] ?? pathname) || "/";
    const currentQs = listingSearchParamsPreserved(searchParams, []).toString();
    const targetQs = listingSearchParamsPreserved(searchParams, []).toString();

    if (currentPath === canonicalTarget && currentQs === targetQs) return;

    const targetUrl = buildLocalizedListingUrl(
      locale,
      canonicalTarget,
      searchParams,
      [],
    );
    router.replace(targetUrl, { scroll: false });
  }, [
    loading,
    filtersReadyForUrlSync,
    selectedCategory,
    sortBy,
    allOffersLabel,
    pathname,
    router,
    searchParams,
    locale,
  ]);

  useEffect(() => {
    if (
      selectedCategory &&
      selectedCategory !== allOffersLabel &&
      !categories.includes(selectedCategory)
    ) {
      setSelectedCategory(allOffersLabel);
    }
  }, [categories, selectedCategory, allOffersLabel]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, sortBy, resolvedLang]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of categories) {
      counts[cat] =
        cat === allOffersLabel
          ? offers.length
          : offers.filter((o) => o.category === cat).length;
    }
    return counts;
  }, [offers, categories, allOffersLabel]);

  const filteredOffers = useMemo(() => {
    const byCategory =
      selectedCategory === allOffersLabel || !selectedCategory
        ? offers
        : offers.filter((o) => o.category === selectedCategory);
    const sorted = [...byCategory].sort((a, b) => {
      if (sortBy === "newest") {
        const d = parseOfferTime(b.dateFrom) - parseOfferTime(a.dateFrom);
        return d !== 0 ? d : a.orderIndex - b.orderIndex;
      }
      if (sortBy === "expiring") {
        const ta = parseOfferTime(a.dateTo);
        const tb = parseOfferTime(b.dateTo);
        const za = ta === 0 ? Infinity : ta;
        const zb = tb === 0 ? Infinity : tb;
        return za - zb || a.orderIndex - b.orderIndex;
      }
      if (sortBy === "discount-high") {
        const d = parseOfferTime(b.dateTo) - parseOfferTime(a.dateTo);
        return d !== 0 ? d : a.orderIndex - b.orderIndex;
      }
      const d = parseOfferTime(a.dateFrom) - parseOfferTime(b.dateFrom);
      return d !== 0 ? d : a.orderIndex - b.orderIndex;
    });
    return sorted;
  }, [offers, selectedCategory, sortBy, allOffersLabel]);

  const totalPages =
    Math.ceil(filteredOffers.length / ITEMS_PER_PAGE) || 1;
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(
    startIndex + ITEMS_PER_PAGE,
    filteredOffers.length
  );
  const paginatedOffers = filteredOffers.slice(startIndex, endIndex);

  return (
    <main className="bg-[#F4F4F4] min-h-screen">
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="mx-auto max-w-[1320px] sm:px-6 px-4 py-6 sm:py-8">
          <nav className="flex items-center gap-2 text-sm text-[#6B7280] mb-4">
            <Link href="/" className="hover:text-[#BD2226] transition-colors">
              {t("nav.home")}
            </Link>
            <span>/</span>
            <span className="text-[#130F0E] font-medium">
              {t("nav.offers")}
            </span>
          </nav>
          <h1 className="text-3xl font-bold text-[#130F0E]">
            {t("offers.title")}
          </h1>
          <p className="text-[#6B7280] mt-2 max-w-2xl">
            {t("offers.subtitle")}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-[1320px] sm:px-6 px-4 pt-0 pb-28 sm:pt-10 sm:pb-10">
        {loadError && (
          <p
            className="mb-2 text-sm text-red-600 sm:mb-6"
            role="alert"
          >
            {t("offers.loadError")}
          </p>
        )}

        <div className="flex flex-col gap-0 lg:flex-row lg:items-start lg:gap-10">
          <aside
            className="hidden lg:block lg:w-[280px] shrink-0"
            aria-label={t("services.desktopFilters")}
          >
            <div className="sticky top-6 rounded-lg border border-[#E5E5E5] bg-white">
              <h2 className="border-b border-[#E5E5E5] px-4 py-3.5 text-base font-bold tracking-tight text-[#130F0E]">
                {t("services.desktopFilters")}
              </h2>

              <div className="border-b border-[#E5E5E5]">
                <button
                  type="button"
                  onClick={() => setDesktopCategoryOpen((o) => !o)}
                  className="flex w-full items-center justify-between gap-2 px-4 py-3.5 text-start transition-colors hover:bg-[#FAFAFA]"
                  aria-expanded={desktopCategoryOpen}
                >
                  <span className="text-[15px] font-bold text-[#130F0E]">
                    {t("services.filterCategory")}
                  </span>
                  <span
                    className="text-lg leading-none text-[#130F0E] tabular-nums"
                    aria-hidden
                  >
                    {desktopCategoryOpen ? "−" : "+"}
                  </span>
                </button>
                {desktopCategoryOpen && (
                  <div className="space-y-0 px-4 pb-4">
                    <div className="mb-2 flex justify-end">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedCategory(allOffersLabel);
                        }}
                        className="text-xs font-medium text-[#2563EB] hover:underline"
                      >
                        {t("services.clearFilters")}
                      </button>
                    </div>
                    <div
                      className="flex flex-col gap-2.5"
                      role="radiogroup"
                      aria-label={t("offers.categoriesAriaLabel")}
                    >
                      {categories.map((category) => {
                        const isActive =
                          selectedCategory === category ||
                          (!selectedCategory && category === categories[0]);
                        const count = categoryCounts[category] ?? 0;
                        return (
                          <label
                            key={category}
                            className="flex cursor-pointer items-start gap-2.5 text-sm text-[#130F0E]"
                          >
                            <input
                              type="radio"
                              name="offers-category-desktop"
                              checked={isActive}
                              onChange={() => setSelectedCategory(category)}
                              className="mt-0.5 h-4 w-4 shrink-0 border-[#D1D5DB] accent-[#130F0E] focus:ring-2 focus:ring-[#2563EB]/30"
                            />
                            <span className="min-w-0 leading-snug">
                              <span className="font-normal">{category}</span>{" "}
                              <span className="text-[#6B7280]">
                                (
                                {count.toLocaleString(
                                  locale === "ar" ? "ar-SA" : "en-US"
                                )}
                                )
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <button
                  type="button"
                  onClick={() => setDesktopSortOpen((o) => !o)}
                  className="flex w-full items-center justify-between gap-2 px-4 py-3.5 text-start transition-colors hover:bg-[#FAFAFA]"
                  aria-expanded={desktopSortOpen}
                >
                  <span className="text-[15px] font-bold text-[#130F0E]">
                    {t("services.filterSort")}
                  </span>
                  <span
                    className="text-lg leading-none text-[#130F0E] tabular-nums"
                    aria-hidden
                  >
                    {desktopSortOpen ? "−" : "+"}
                  </span>
                </button>
                {desktopSortOpen && (
                  <div
                    className="flex flex-col gap-2.5 px-4 pb-4"
                    role="radiogroup"
                    aria-label={t("services.sortBy")}
                  >
                    {sortOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex cursor-pointer items-start gap-2.5 text-sm text-[#130F0E]"
                      >
                        <input
                          type="radio"
                          name="offers-sort-desktop"
                          checked={sortBy === opt.value}
                          onChange={() => setSortBy(opt.value)}
                          className="mt-0.5 h-4 w-4 shrink-0 border-[#D1D5DB] accent-[#130F0E] focus:ring-2 focus:ring-[#2563EB]/30"
                        />
                        <span className="leading-snug">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </aside>

          <div className="min-w-0 w-full flex-1">
            <div className="mb-0 hidden sm:mb-8 sm:flex sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-3 lg:hidden">
              <div className="flex items-center gap-2">
                <label
                  htmlFor="sort-offers"
                  className="shrink-0 text-sm font-medium text-[#6B7280]"
                >
                  {t("services.sortBy")}
                </label>
                <select
                  id="sort-offers"
                  value={sortBy}
                  onChange={(e) =>
                    setSortBy(e.target.value as SortByValue)
                  }
                  className="min-w-[10rem] rounded-lg border border-[#E5E5E5] bg-white px-4 py-2.5 text-sm text-[#130F0E] focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

        <MobileEndDrawer
          open={mobileFiltersOpen}
          onClose={() => setMobileFiltersOpen(false)}
          title={t("services.desktopFilters")}
          closeLabel={t("notifications.close")}
        >
          <div className="flex flex-col gap-6">
            <div>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-[15px] font-bold text-[#130F0E]">
                  {t("services.filterCategory")}
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCategory(allOffersLabel);
                  }}
                  className="text-xs font-medium text-[#2563EB] hover:underline"
                >
                  {t("services.clearFilters")}
                </button>
              </div>
              <div
                className="flex flex-col gap-2.5"
                role="radiogroup"
                aria-label={t("offers.categoriesAriaLabel")}
              >
                {categories.map((category) => {
                  const isActive =
                    selectedCategory === category ||
                    (!selectedCategory && category === categories[0]);
                  const count = categoryCounts[category] ?? 0;
                  return (
                    <label
                      key={category}
                      className="flex cursor-pointer items-start gap-2.5 text-sm text-[#130F0E]"
                    >
                      <input
                        type="radio"
                        name="offers-category-mobile-drawer"
                        checked={isActive}
                        onChange={() => setSelectedCategory(category)}
                        className="mt-0.5 h-4 w-4 shrink-0 border-[#D1D5DB] accent-[#130F0E] focus:ring-2 focus:ring-[#2563EB]/30"
                      />
                      <span className="min-w-0 leading-snug">
                        <span className="font-normal">{category}</span>{" "}
                        <span className="text-[#6B7280]">
                          (
                          {count.toLocaleString(
                            locale === "ar" ? "ar-SA" : "en-US"
                          )}
                          )
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-[#E5E5E5] pt-6">
              <h3 className="mb-3 text-[15px] font-bold text-[#130F0E]">
                {t("services.filterSort")}
              </h3>
              <div
                className="flex flex-col gap-2.5"
                role="radiogroup"
                aria-label={t("services.sortBy")}
              >
                {sortOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-start gap-2.5 text-sm text-[#130F0E]"
                  >
                    <input
                      type="radio"
                      name="offers-sort-mobile-drawer"
                      checked={sortBy === opt.value}
                      onChange={() => setSortBy(opt.value)}
                      className="mt-0.5 h-4 w-4 shrink-0 border-[#D1D5DB] accent-[#130F0E] focus:ring-2 focus:ring-[#2563EB]/30"
                    />
                    <span className="leading-snug">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </MobileEndDrawer>

        {loading ? (
          <OfferListingSkeletonGrid count={8} />
        ) : filteredOffers.length === 0 ? (
          <p className="text-center text-[#6B7280] py-16">
            {t("offers.empty")}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 animate-fade-slide-up sm:mt-0 mt-5">
            {paginatedOffers.map((offer) => (
              <OfferListingCard
                key={offer.id}
                offer={offer}
                href={buildOfferDetailPath(offer.name, offer.id, locale, offer.slug)}
                specialOfferLabel={t("offers.specialOffer")}
                expiredLabel={t("offers.filterExpired")}
                couponAvailableLabel={t("offers.couponAvailable")}
                pageName="OFFER LISTING"
                pageCategory="OFFER"
                eventCategory="OFFERLEADFORM"
              />
            ))}
          </div>
        )}

        {!loading && filteredOffers.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={filteredOffers.length}
            startIndex={startIndex}
            endIndex={endIndex}
            itemLabel={t("pagination.offers")}
          />
        )}

        <button
          type="button"
          onClick={() => setMobileFiltersOpen(true)}
          className="fixed z-40 flex h-14 w-14 items-center justify-center rounded-full bg-[#BD2226] text-white shadow-[0_4px_14px_rgba(221,4,40,0.45)] transition-transform hover:scale-105 active:scale-95 lg:hidden end-6 bottom-[max(1.5rem,env(safe-area-inset-bottom))]"
          aria-label={t("services.mobileOpenFilters")}
        >
          <FilterToolbarIcon className="text-white" />
        </button>
          </div>
        </div>
      </div>
    </main>
  );
}
